import { Spinner } from '@/components/glitch-ui/spinner';
import { useState, useMemo, useRef, useEffect } from 'react';
import { createFileRoute, useNavigate, useLocation } from '@tanstack/react-router';
import { ArrowRight, Palette, Sparkles, Users, Globe, Trash2, Plus } from 'lucide-react';
import { ImageGallery } from '@/components/glitch-ui/image-gallery';
import { DocumentsLibrary } from '@/components/business/documents-library';
import { usePostHog } from 'posthog-js/react';
import { Button } from '@/components/ui/button';
import { AddLandingPageModal } from '@/components/assets/add-landing-page-modal';
import { GeneratedLandingPageDetail, LANDING_PAGE_STATUS_BADGE } from '@/components/landing-pages/generated-landing-pages';
import { FilterDropdown } from '@/components/glitch-ui/filter-dropdown';
import { businessLandingPagesQueryOptions } from '@/lib/query-options';
import { GenerateLandingPageDialog } from '@/components/landing-pages/generate-landing-page-dialog';
import { TabCardPanel } from '@/components/glitch-ui/tab-card-panel';
import { EmptyState, PageErrorState } from '@/components/glitch-ui/empty-state';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { organizationDataAtom, selectedAssetsTabAtom, selectedAssetsGoogleSubTabAtom, mediaViewModeAtom, productsUrlFilterAtom, googleAdsAccountAtom, googleAdsAccountResolvedAtom } from '@/store';
import { useTabPersistence } from '@/hooks/useTabPersistence';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getLandingPagesDetails, deleteLandingPage } from '@/lib/apis';
import type { LandingPageDetails } from '@/lib/apis';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  businessQueryOptions,
  accountSitelinksQueryOptions,
  accountCalloutsQueryOptions,
  campaignSitelinksQueryOptions,
  campaignCalloutsQueryOptions,
  allCampaignIpExclusionsQueryOptions,
  negativeKeywordListsQueryOptions,
  accountIpExclusionsQueryOptions,
} from '@/lib/query-options';
import { AccountCallouts, type AccountCalloutsRef } from '@/components/settings/account/account-callouts';
import { AccountSitelinks, type AccountSitelinksRef } from '@/components/settings/account/account-sitelinks';
import { NegativeKeywordLists, NegativeKeywordDetail, type NegativeKeywordListsRef } from '@/components/settings/account/negative-keyword-lists';
import { AccountIpExclusions, type AccountIpExclusionsRef } from '@/components/settings/account/account-ip-exclusions';
import { ConversionManagement } from '@/components/settings/conversion-management';
import { useTestMode } from '@/hooks/useTestMode';
import { withOrgReset } from '@/components/glitch-ui/with-org-reset';
import { ConformationModal } from '@/components/glitch-ui/confirmation-modal';
import { DataTable } from '@/components/glitch-ui/data-table';
import type { ColumnDef } from '@tanstack/react-table';
import { BulkSelectCheckbox, useBulkSelection } from '@/components/glitch-ui/bulk-select';
import { normalizeUrl } from '@/components/glitch-ui/business-landing-page-combobox';
import { useToast } from '@/hooks/useToast';

export const Route = createFileRoute('/_authenticated/_layout/assets/')({
  component: withOrgReset(AssetsPage),
  staticData: {
    title: 'Assets',
    icon: Palette,
  },
});

// Tab configuration
const TAB_IDS = ['landing-pages', 'media', 'documents', 'conversions', 'google', 'audiences'] as const;
type TabId = typeof TAB_IDS[number];

const allTabs = [
  { id: 'landing-pages' as const, label: 'Landing Pages', testModeOnly: false },
  { id: 'media' as const, label: 'Media', testModeOnly: false },
  { id: 'documents' as const, label: 'Documents', testModeOnly: false },
  { id: 'conversions' as const, label: 'Conversions', testModeOnly: false },
  { id: 'google' as const, label: 'Google', testModeOnly: false },
  { id: 'audiences' as const, label: 'Audiences', testModeOnly: true },
];


// Helper to extract page title from URL
function getPageTitle(url: string): string {
  try {
    const urlObj = new URL(url);
    const path = urlObj.pathname;

    // If it's the root, return "Home"
    if (path === '/' || path === '') {
      return 'Home';
    }

    // Get the last segment of the path
    const segments = path.split('/').filter(Boolean);
    const lastSegment = segments[segments.length - 1] || 'Home';

    // Convert slug to title case (e.g., "about-us" -> "About Us")
    return lastSegment
      .replace(/-/g, ' ')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());
  } catch {
    return 'Page';
  }
}

// Helper to generate screenshot URL using a thumbnail service
function getScreenshotUrl(url: string): string {
  // Using a free screenshot service - you could replace with your own
  return `https://api.microlink.io/?url=${encodeURIComponent(url)}&screenshot=true&meta=false&embed=screenshot.url`;
}

// #4526 — a landing page can't be removed while it's referenced. Returns true when any ad,
// sitelink, or product points at the URL.
function isLandingPageReferenced(lp: LandingPageDetails): boolean {
  return lp.ad_count > 0 || lp.sitelink_count > 0 || (lp.product_count ?? 0) > 0;
}

function landingPageReferenceMessage(lp: LandingPageDetails): string {
  const parts: string[] = [];
  if (lp.ad_count > 0) parts.push(`${lp.ad_count} ad${lp.ad_count === 1 ? '' : 's'}`);
  if (lp.sitelink_count > 0) parts.push(`${lp.sitelink_count} sitelink${lp.sitelink_count === 1 ? '' : 's'}`);
  const productCount = lp.product_count ?? 0;
  if (productCount > 0) parts.push(`${productCount} product${productCount === 1 ? '' : 's'}`);
  return `Deletion is blocked while this URL is used by ${parts.join(', ')}.`;
}

function AssetsPage() {
  const posthog = usePostHog();
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedUrl, setSelectedUrl] = useState<string | null>(null);
  const [selectedNegativeListId, setSelectedNegativeListId] = useState<string | null>(null);
  const [deleteUrl, setDeleteUrl] = useState<string | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  // FE-6 (#6480): the persona/offer generate dialog, opened from the Generated sub-tab's button.
  const [isGenerateLandingPageOpen, setIsGenerateLandingPageOpen] = useState(false);
  const [failedThumbnailUrls, setFailedThumbnailUrls] = useState<Set<string>>(new Set());
  // Toolbar handed up from ImageGallery so the media controls sit inline with the tab bar.
  const [mediaControls, setMediaControls] = useState<React.ReactNode>(null);
  const [documentsControls, setDocumentsControls] = useState<React.ReactNode>(null);
  const [conversionsControls, setConversionsControls] = useState<React.ReactNode>(null);
  const mediaViewMode = useAtomValue(mediaViewModeAtom);
  const organizationData = useAtomValue(organizationDataAtom);
  const googleAdsAccount = useAtomValue(googleAdsAccountAtom);
  const testMode = useTestMode();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  // Google sub-tabs mirror the campaign Misc tab order: Sitelinks → Callouts → Negatives.
  // Callouts is the only one available outside test mode.
  type GoogleSubTab = 'sitelinks' | 'callouts' | 'negatives' | 'ip-exclusions';
  const GOOGLE_SUB_TABS: GoogleSubTab[] = ['sitelinks', 'callouts', 'negatives', 'ip-exclusions'];
  const [savedGoogleSubTab, setSavedGoogleSubTab] = useAtom(selectedAssetsGoogleSubTabAtom);
  // The generated side of Landing Pages (Generate, the source filter, generated rows) shows for
  // everyone (#6681); the backend's landing-pages-enabled flag stays the access control.
  const canGenerateLandingPages = true;
  // Landing Pages is one list (#6674): the pages found on the site and the ones Glitch
  // generated, told apart by a source filter and the generated rows' mark.
  const [landingSource, setLandingSource] = useState<Record<string, string[]>>({ source: [] });
  const [selectedGeneratedId, setSelectedGeneratedId] = useState<string | null>(null);
  const [googleSubTab, setGoogleSubTab] = useState<GoogleSubTab>(() => {
    const initial = (window.location.hash || '').replace('#', '').split('/');
    if (initial[0] === 'google' && GOOGLE_SUB_TABS.includes(initial[1] as GoogleSubTab)) {
      return initial[1] as GoogleSubTab;
    }
    return GOOGLE_SUB_TABS.includes(savedGoogleSubTab as GoogleSubTab) ? savedGoogleSubTab as GoogleSubTab : 'callouts';
  });
  // Section refs let the hoisted Add button trigger each section's existing add-modal logic.
  const accountSitelinksRef = useRef<AccountSitelinksRef>(null);
  const accountCalloutsRef = useRef<AccountCalloutsRef>(null);
  const accountNegativesRef = useRef<NegativeKeywordListsRef>(null);
  const accountIpExclusionsRef = useRef<AccountIpExclusionsRef>(null);
  // Counts for the tab badges — reuse the shared account-asset query options so these share the
  // section components' cache (no duplicate fetches) and refetch on account switch the same way.
  const accountId = googleAdsAccount?.googleads_account_id;
  const googleAdsAccountResolved = useAtomValue(googleAdsAccountResolvedAtom);
  const { data: accountSitelinksData, isPending: sitelinksPending } = useQuery(accountSitelinksQueryOptions(accountId));
  // The campaign halves of the same two lists (#6745), read here so the
  // toolbar's filter can offer the campaigns that actually carry an asset.
  const { data: campaignSitelinksData } = useQuery(campaignSitelinksQueryOptions(accountId));
  const { data: campaignCalloutsData } = useQuery(campaignCalloutsQueryOptions(accountId));
  const { data: campaignIpExclusionsData } = useQuery(allCampaignIpExclusionsQueryOptions(accountId));
  const { data: accountCalloutsData, isPending: calloutsPending } = useQuery(accountCalloutsQueryOptions(accountId));
  const { data: accountNegativesData, isPending: negativesPending } = useQuery(negativeKeywordListsQueryOptions(accountId));
  const { data: accountIpExclusionsData, isPending: ipExclusionsPending } = useQuery(accountIpExclusionsQueryOptions(accountId));
  // While the account atom hydrates (queries disabled, isPending true with no fetch) or the
  // four Google queries load, the tab must show a loading state — the counts all read 0
  // during that window, which flashed the "No Google Assets" empty state on boot (#5820).
  const googleDataPending =
    !googleAdsAccountResolved ||
    (!!accountId && (sitelinksPending || calloutsPending || negativesPending || ipExclusionsPending));

  // Filter tabs based on test mode
  const tabs = useMemo(() => {
    return allTabs.filter(tab => !tab.testModeOnly || testMode);
  }, [testMode]);

  const visibleTabIds = tabs.map(t => t.id) as TabId[];
  // Hash format: `<tab>` or `<tab>/<sub-tab>` — strip the sub-tab before passing to the hook.
  const [hashMain, hashSubTab] = (location.hash?.replace('#', '') || '').split('/');
  const hashForHook = hashMain ? `#${hashMain}` : '';
  const { activeTab, handleTabChange } = useTabPersistence(
    selectedAssetsTabAtom,
    visibleTabIds,
    'landing-pages',
    (tabId, replace) => navigate({ to: '/assets', hash: tabId, replace }),
    hashForHook || location.hash,
  );

  // Keep `googleSubTab` in sync with the URL when navigating between hashes inside the app
  // (the useState lazy init above only covers fresh page loads).
  useEffect(() => {
    if (hashMain === 'google' && hashSubTab && GOOGLE_SUB_TABS.includes(hashSubTab as GoogleSubTab)) {
      setGoogleSubTab(hashSubTab as GoogleSubTab);
      setSavedGoogleSubTab(hashSubTab as GoogleSubTab);
    }
  }, [hashMain, hashSubTab]);

  // Fetch business data for the current organization
  const { data: business, isError: isBusinessError, isPending: isBusinessPending, refetch: refetchBusiness } = useQuery({
    ...businessQueryOptions(organizationData?.id || 0),
    enabled: !!organizationData?.id,
  });

  // Fetch landing pages details with scraped data
  const { data: landingPagesDetails = [], isLoading: isLandingPagesLoading } = useQuery({
    queryKey: ['landingPagesDetails', business?.id],
    queryFn: () => {
      if (!business?.id) return Promise.resolve([]);
      return getLandingPagesDetails(business.id);
    },
    enabled: !!business?.id,
  });

  const isLoadingLandingPages = !!organizationData?.id && (isBusinessPending || isLandingPagesLoading);

  // Set of onboarding link URLs (only these can be deleted)
  const onboardingLinks = useMemo(
    () => new Set(business?.onboarding_links || []),
    [business?.onboarding_links],
  );

  const deleteMutation = useMutation({
    mutationFn: (url: string) => deleteLandingPage(business!.id, url),
    onSuccess: () => {
      posthog?.capture('landing_page_deleted');
      queryClient.invalidateQueries({ queryKey: ['landingPagesDetails', business?.id] });
      queryClient.invalidateQueries({ queryKey: ['organizations', organizationData?.id, 'business'] });
      if (selectedUrl === deleteUrl) setSelectedUrl(null);
      setDeleteUrl(null);
    },
    onError: () => {
      toast({ description: 'Failed to delete landing page', variant: 'destructive' });
    },
  });

  // Get selected landing page details
  const selectedLandingPage = selectedUrl
    ? landingPagesDetails.find(lp => lp.url === selectedUrl)
    : null;

  // Negatives uses the same master-detail layout as landing pages: the list lives in the card,
  // its keywords render in a sibling column outside the card. Only when lists exist (otherwise the
  // Google tab shows its single empty state).
  const { data: generatedPages = [], isLoading: isGeneratedLoading } = useQuery({
    ...businessLandingPagesQueryOptions(business?.id ?? 0, organizationData?.id ?? 0),
    enabled: canGenerateLandingPages && !!business?.id && !!organizationData?.id,
  });
  // A failed generation is not a usable asset: it stays out of the list, though a page that
  // errors while open still shows its error.
  const visibleGeneratedPages = generatedPages.filter((page) => page.status !== 'ERROR');
  const selectedGeneratedPage = selectedGeneratedId
    ? generatedPages.find((page) => page.public_id === selectedGeneratedId) ?? null
    : null;
  const sourceFilter = landingSource.source ?? [];
  const showSitePages = sourceFilter.length === 0 || sourceFilter.includes('site');
  const showGeneratedPages = canGenerateLandingPages && (sourceFilter.length === 0 || sourceFilter.includes('generated'));
  const hasAnyLandingPages = landingPagesDetails.length > 0 || visibleGeneratedPages.length > 0;

  // The table's rows (#6484): a site page or a generated page. The root
  // domain sits first by default; a sort the reader picks takes over.
  type GeneratedPageRow = (typeof visibleGeneratedPages)[number];
  type LandingRow =
    | { kind: 'site'; key: string; page: LandingPageDetails; root: boolean }
    | { kind: 'generated'; key: string; generated: GeneratedPageRow; root: false };
  const rootUrl = business?.business_url ? normalizeUrl(business.business_url) : null;
  const landingRows = useMemo<LandingRow[]>(() => {
    const site = (showSitePages ? landingPagesDetails : []).map(
      (page): LandingRow => ({ kind: 'site', key: page.url, page, root: rootUrl !== null && normalizeUrl(page.url) === rootUrl }),
    );
    site.sort((a, b) => Number(b.root) - Number(a.root));
    const generated = (showGeneratedPages ? visibleGeneratedPages : []).map(
      (page): LandingRow => ({ kind: 'generated', key: `generated-${page.public_id}`, generated: page, root: false }),
    );
    return [...site, ...generated];
  }, [showSitePages, landingPagesDetails, showGeneratedPages, visibleGeneratedPages, rootUrl]);
  // A page can be removed when it is one of several onboarding links and
  // nothing built on it still points at it (the same rule the bin follows).
  const canDeleteLandingPage = (page: LandingPageDetails) =>
    onboardingLinks.has(page.url) && onboardingLinks.size > 1 && landingPagesDetails.length > 1 && !isLandingPageReferenced(page);
  const landingSelection = useBulkSelection(
    landingRows,
    (row) => row.key,
    (row) => row.kind === 'site' && canDeleteLandingPage(row.page),
  );
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const bulkDeleteLandingPages = async () => {
    setBulkDeleting(true);
    try {
      // One at a time: each delete re-checks the page's references upstream.
      for (const url of landingSelection.selected) await deleteMutation.mutateAsync(url);
      landingSelection.clear();
      setBulkDeleteOpen(false);
    } finally {
      setBulkDeleting(false);
    }
  };
  // The counts link into the views that hold the rows (#6484): Sitelinks on
  // the Google tab, Products on Business, each filtered to this page's URL.
  const setProductsUrlFilter = useSetAtom(productsUrlFilterAtom);
  const openSitelinksFor = (url: string) => {
    // The page arrives as a filter selection, which the reader can see and
    // widen in the same control as every other filter (#6745).
    setSitelinkFilter({ pages: [url] });
    setGoogleSubTab('sitelinks');
    setSavedGoogleSubTab('sitelinks');
    handleTabChange('google');
  };
  const openProductsFor = (url: string) => {
    setProductsUrlFilter(url);
    navigate({ to: '/business', hash: 'product' });
  };
  const countCell = (count: number, onOpen?: () => void) =>
    count > 0 && onOpen ? (
      <button
        type="button"
        className="text-sm text-[#290BD9] hover:text-[#2309C5] hover:underline"
        onClick={(e) => {
          e.stopPropagation();
          onOpen();
        }}
      >
        {count}
      </button>
    ) : (
      <span className="text-sm text-gray-900">{count > 0 ? count : '-'}</span>
    );
  // Select all earns its place only when there is more than one row to tick:
  // with a single row the box beside it is the same click (#6724).
  const selectableCount = landingRows.filter((row) => row.kind === 'site' && canDeleteLandingPage(row.page)).length;
  const landingColumns: ColumnDef<LandingRow>[] = [
    {
      id: 'select',
      enableSorting: false,
      meta: { className: 'pl-6 pr-0 w-10' },
      header: () =>
        selectableCount > 1 ? (
          <BulkSelectCheckbox
            checked={landingSelection.allSelected}
            indeterminate={landingSelection.someSelected}
            ariaLabel="Select all landing pages"
            onChange={landingSelection.toggleAll}
          />
        ) : null,
      cell: ({ row }) =>
        row.original.kind === 'site' && canDeleteLandingPage(row.original.page) ? (
          <BulkSelectCheckbox
            checked={landingSelection.isSelected(row.original.key)}
            ariaLabel={`Select ${row.original.page.title || getPageTitle(row.original.page.url)}`}
            onChange={() => landingSelection.toggle(row.original.key)}
          />
        ) : null,
    },
    {
      id: 'thumbnail',
      enableSorting: false,
      meta: { className: 'px-6 w-36' },
      header: () => null,
      cell: ({ row }) =>
        row.original.kind === 'site' ? (
          <a
            href={row.original.page.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className={`block w-24 h-16 rounded-md overflow-hidden border border-gray-200 bg-gray-100 hover:border-[#290BD9] transition-colors cursor-pointer ${
              failedThumbnailUrls.has(row.original.page.url) ? 'flex items-center justify-center' : ''
            }`}
          >
            {failedThumbnailUrls.has(row.original.page.url) ? (
              <Globe className="h-6 w-6 text-gray-400" strokeWidth={1.5} />
            ) : (
              <img
                src={getScreenshotUrl(row.original.page.url)}
                alt={`Preview of ${getPageTitle(row.original.page.url)}`}
                className="w-full h-full object-cover object-top"
                onError={() => {
                  const url = row.original.kind === 'site' ? row.original.page.url : '';
                  setFailedThumbnailUrls((prev) => new Set(prev).add(url));
                }}
              />
            )}
          </a>
        ) : (
          <div className="flex h-16 w-24 items-center justify-center rounded-md border border-[#C4B5FD] bg-[#EDE9FE]">
            <Sparkles className="h-5 w-5 text-[#290BD9]" />
          </div>
        ),
    },
    {
      id: 'title',
      accessorFn: (row) => (row.kind === 'site' ? row.page.title || getPageTitle(row.page.url) : row.generated.public_id),
      header: 'Page Title',
      meta: { className: 'pl-0 pr-6 whitespace-normal' },
      cell: ({ row }) => {
        if (row.original.kind === 'generated') {
          const page = row.original.generated;
          const badge = LANDING_PAGE_STATUS_BADGE[page.status];
          return (
            <>
              <div className="flex items-center gap-2">
                <span className="text-base font-medium text-gray-900">{page.public_id}</span>
                <span className={`inline-flex items-center rounded px-2 py-0.5 text-xs ${badge.className}`}>{badge.label}</span>
              </div>
              <div className="text-sm text-gray-500">
                Generated by Glitch · {page.leads_count} {page.leads_count === 1 ? 'lead' : 'leads'}
              </div>
            </>
          );
        }
        const page = row.original.page;
        return (
          <>
            <div className="text-base font-medium text-gray-900">{page.title || getPageTitle(page.url)}</div>
            <a
              href={page.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="text-sm text-gray-500 hover:text-[#290BD9] transition-colors block max-w-xs truncate"
              title={page.url}
            >
              {page.url}
            </a>
          </>
        );
      },
    },
    {
      id: 'sitelinks',
      accessorFn: (row) => (row.kind === 'site' ? row.page.sitelink_count : -1),
      header: 'Sitelinks',
      meta: { className: 'px-6 w-24 text-right' },
      cell: ({ row }) =>
        row.original.kind === 'site' ? countCell(row.original.page.sitelink_count, () => openSitelinksFor((row.original as { page: LandingPageDetails }).page.url)) : <span className="text-sm text-gray-900">-</span>,
    },
    {
      id: 'ads',
      accessorFn: (row) => (row.kind === 'site' ? row.page.ad_count : -1),
      header: 'Ads',
      meta: { className: 'px-6 w-24 text-right' },
      cell: ({ row }) => (row.original.kind === 'site' ? countCell(row.original.page.ad_count) : <span className="text-sm text-gray-900">-</span>),
    },
    {
      id: 'products',
      accessorFn: (row) => (row.kind === 'site' ? row.page.product_count : -1),
      header: 'Products',
      meta: { className: 'px-6 w-24 text-right' },
      cell: ({ row }) =>
        row.original.kind === 'site' ? countCell(row.original.page.product_count, () => openProductsFor((row.original as { page: LandingPageDetails }).page.url)) : <span className="text-sm text-gray-900">-</span>,
    },
    {
      id: 'remove',
      enableSorting: false,
      meta: { className: 'px-3 w-12' },
      header: () => null,
      cell: ({ row }) => {
        if (row.original.kind !== 'site') return null;
        const page = row.original.page;
        if (!(onboardingLinks.has(page.url) && onboardingLinks.size > 1 && landingPagesDetails.length > 1)) return null;
        return isLandingPageReferenced(page) ? (
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex" onClick={(e) => e.stopPropagation()}>
                  <button type="button" disabled className="p-1 rounded text-gray-300 cursor-not-allowed">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <p className="max-w-[260px] text-xs">{landingPageReferenceMessage(page)}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setDeleteUrl(page.url);
            }}
            className="p-1 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
            title="Delete landing page"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        );
      },
    },
  ];
  // The tab's toolbar: the source filter, Add a page from the site, Generate one.
  const landingPageActions = business ? (
    <>
      {canGenerateLandingPages && (
        <FilterDropdown
          sections={[
            {
              id: 'source',
              title: 'Source',
              options: [
                { value: 'site', label: 'From your site' },
                { value: 'generated', label: 'Generated' },
              ],
            },
          ]}
          selectedValues={landingSource}
          showDateRange={false}
          popoverWidth="w-[220px]"
          activeFilterCount={sourceFilter.length}
          onApply={(filters) => setLandingSource(filters.selections)}
        />
      )}
      <Button variant="outline" className="text-gray-900 h-9" onClick={() => setIsAddModalOpen(true)}>
        <Plus className="h-4 w-4 mr-2" />
        Add
      </Button>
      {canGenerateLandingPages && (
        <Button variant="outline" className="text-gray-900 h-9" onClick={() => setIsGenerateLandingPageOpen(true)}>
          <Sparkles className="h-4 w-4 mr-2" />
          Generate
        </Button>
      )}
    </>
  ) : undefined;
  // The Google tab's sections and the Add button for the open one, drawn in the toolbar (#6681).
  const googleSubTabConfigs: { id: GoogleSubTab; label: string; count: number; max: number | null; addLabel: string; triggerAdd: () => void }[] = [
    { id: 'sitelinks', label: 'Sitelinks', count: accountSitelinksData?.sitelinks?.length ?? 0, max: 20, addLabel: 'Add Sitelink', triggerAdd: () => accountSitelinksRef.current?.openAdd() },
    { id: 'callouts', label: 'Callouts', count: accountCalloutsData?.callouts?.length ?? 0, max: 20, addLabel: 'Add Callout', triggerAdd: () => accountCalloutsRef.current?.openAdd() },
    { id: 'negatives', label: 'Negative Lists', count: accountNegativesData?.lists?.length ?? 0, max: 20, addLabel: 'New List', triggerAdd: () => accountNegativesRef.current?.openAdd() },
    { id: 'ip-exclusions', label: 'IP Exclusions', count: accountIpExclusionsData?.exclusions?.length ?? 0, max: null, addLabel: 'Add IPs', triggerAdd: () => accountIpExclusionsRef.current?.openAdd() },
  ];
  // Sub-tabs, and the Add button, wait until at least one section has content: the first-time
  // surface is one tall empty state.
  const googleTotalCount = googleSubTabConfigs.reduce((sum, t) => sum + t.count, 0);
  const activeGoogleSubTab: GoogleSubTab = googleSubTabConfigs.some((t) => t.id === googleSubTab) ? googleSubTab : 'callouts';
  const activeGoogleConfig = googleSubTabConfigs.find((t) => t.id === activeGoogleSubTab);
  // The Google lists hold account assets and campaign assets side by side
  // (#6745), so the toolbar filters on where an asset is set, and sitelinks
  // also on the page they point at. This replaces the "Sitelinks for <url> /
  // Show all" banner the landing-page counts used to raise.
  const [sitelinkFilter, setSitelinkFilter] = useState<Record<string, string[]>>({});
  const [calloutFilter, setCalloutFilter] = useState<Record<string, string[]>>({});
  const [ipExclusionFilter, setIpExclusionFilter] = useState<Record<string, string[]>>({});

  // A campaign reads by the part of its name that is not the business, which
  // is the same on every row. The sitelinks and callouts lists spell it the
  // same way.
  const campaignFilterLabel = (name: string): string => {
    const parts = name.split(' | ');
    return parts.length >= 3 ? parts.slice(1).join(' | ') : name;
  };

  const campaignOptionsFrom = (
    groups: { campaigns: { id: number; name: string }[] }[],
  ): { value: string; label: string }[] => {
    const byId = new Map<number, string>();
    for (const group of groups) {
      for (const campaign of group.campaigns) byId.set(campaign.id, campaignFilterLabel(campaign.name));
    }
    return [...byId.entries()]
      .map(([id, label]) => ({ value: String(id), label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  };

  const sitelinkSourceOptions = [
    { value: 'account', label: 'Account' },
    ...campaignOptionsFrom(campaignSitelinksData?.sitelinks ?? []),
  ];
  const sitelinkPageOptions = [
    ...new Set([
      ...(accountSitelinksData?.sitelinks ?? []).map((sl) => sl.url),
      ...(campaignSitelinksData?.sitelinks ?? []).map((sl) => sl.url),
    ]),
  ]
    .sort()
    .map((url) => ({ value: url, label: url }));
  const calloutSourceOptions = [
    { value: 'account', label: 'Account' },
    ...campaignOptionsFrom(campaignCalloutsData?.callouts ?? []),
  ];
  const ipExclusionSourceOptions = [
    { value: 'account', label: 'Account' },
    ...campaignOptionsFrom(campaignIpExclusionsData?.exclusions ?? []),
  ];

  const googleFilterConfig =
    activeGoogleSubTab === 'sitelinks'
      ? {
          sections: [
            { id: 'source', title: 'Source', options: sitelinkSourceOptions },
            { id: 'pages', title: 'Landing page', options: sitelinkPageOptions },
          ],
          selected: sitelinkFilter,
          onApply: setSitelinkFilter,
        }
      : activeGoogleSubTab === 'callouts'
        ? {
            sections: [{ id: 'source', title: 'Source', options: calloutSourceOptions }],
            selected: calloutFilter,
            onApply: setCalloutFilter,
          }
        : activeGoogleSubTab === 'ip-exclusions'
          ? {
              sections: [{ id: 'source', title: 'Source', options: ipExclusionSourceOptions }],
              selected: ipExclusionFilter,
              onApply: setIpExclusionFilter,
            }
          : null;
  const googleFilterCount = googleFilterConfig
    ? Object.values(googleFilterConfig.selected).reduce((n, v) => n + v.length, 0)
    : 0;

  const googleActions =
    activeGoogleConfig && !googleDataPending && googleTotalCount > 0 ? (
      <>
        {googleFilterConfig && (
          <FilterDropdown
            sections={googleFilterConfig.sections}
            selectedValues={googleFilterConfig.selected}
            showDateRange={false}
            popoverWidth="w-[280px]"
            activeFilterCount={googleFilterCount}
            onApply={(filters) => googleFilterConfig.onApply(filters.selections)}
          />
        )}
        <Button
          variant="outline"
          className="text-gray-900 h-9"
          onClick={activeGoogleConfig.triggerAdd}
          disabled={activeGoogleConfig.max != null && activeGoogleConfig.count >= activeGoogleConfig.max}
        >
          <Plus className="h-4 w-4 mr-2" />
          {activeGoogleConfig.addLabel}
        </Button>
      </>
    ) : undefined;
  const negativeListCount = accountNegativesData?.lists?.length ?? 0;
  const isNegativesDetail = activeTab === 'google' && googleSubTab === 'negatives' && negativeListCount > 0;

  if (isBusinessError) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-between py-0.5">
          <div className="flex items-center gap-3"></div>
        </div>
        <div className="flex-1">
          <PageErrorState
            title="Couldn't Load Assets"
            description="This page failed to load. This is a loading error, not missing data, so nothing has been lost. Retry, or contact support if it persists."
            onRetry={() => refetchBusiness()}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between py-0.5">
        <div className="flex items-center gap-3">
        </div>
      </div>

      <div className="flex-1 space-y-6">
        {/* Landing Pages (Known sub-tab) with data: 9/12 + 3/12 Grid layout */}
        {/* Every tab in one card; Negatives adds a sibling detail column outside it. */}
          <div className={isNegativesDetail ? 'grid grid-cols-12 gap-1' : ''}>
          <div className={isNegativesDetail ? 'col-span-8 min-w-0' : ''}>
          <TabCardPanel
            tabs={tabs.map((tab) => ({
              id: tab.id,
              label: tab.label,
              isActive: activeTab === tab.id,
              onClick: () => handleTabChange(tab.id),
            }))}
            noPadding={activeTab === 'media' && mediaViewMode === 'table'}
            /* Conversions keeps the card's side padding and loses only the gap
               above its header row (#6715), so its table starts where the
               Landing Pages and Competitors tables do. */
            contentClassName={activeTab === 'conversions' ? 'pt-0' : undefined}
            actions={
              activeTab === 'landing-pages' ? (
                landingPageActions
              ) : activeTab === 'media' ? (
                mediaControls ?? undefined
              ) : activeTab === 'documents' ? (
                documentsControls ?? undefined
              ) : activeTab === 'conversions' ? (
                conversionsControls ?? undefined
              ) : activeTab === 'google' ? (
                googleActions
              ) : undefined
            }
          >
                {/* Landing Pages (#6674, #6681): the same table Competitors and Conversions use, with
                    the selected page's details beside it inside the card, as Competitors has. A generated
                    page opens in full in place of the table. */}
                {activeTab === 'landing-pages' && (
                  selectedGeneratedPage && business?.id ? (
                    <GeneratedLandingPageDetail
                      page={selectedGeneratedPage}
                      organizationId={organizationData?.id ?? 0}
                      onBack={() => setSelectedGeneratedId(null)}
                      onChanged={() => queryClient.invalidateQueries({ queryKey: ['landing-pages-by-business', business.id] })}
                    />
                  ) : isLoadingLandingPages || isGeneratedLoading ? (
                    <div className="flex items-center justify-center gap-3 min-h-[50vh]">
                      <Spinner size="md" className="text-gray-500" />
                      <span className="text-gray-500">Loading landing pages...</span>
                    </div>
                  ) : !hasAnyLandingPages ? (
                    <EmptyState
                      icon={<Globe className="h-12 w-12 text-gray-500" strokeWidth={1} />}
                      title="No Landing Pages"
                      description="Pages on your site are found when your business is created. Add one, or generate one."
                    />
                  ) : (
                    <div className="grid grid-cols-12 gap-0 -m-6">
                      <div className="col-span-9 border-r border-gray-200">
                        {/* The house table (#6484): click-to-sort on every data column, the
                            root domain first until a sort is picked, checkboxes for a bulk
                            delete (#6463). Padded so its flush edges meet the column's. */}
                        <div className="px-6 pb-6">
                          <DataTable<LandingRow>
                            data={landingRows}
                            columns={landingColumns}
                            variant="flush"
                            pageSize={999}
                            getRowId={(row) => row.key}
                            /* Ticked rows are acted on from the head (#6724). */
                            bulk={{
                              count: landingSelection.count,
                              itemName: 'landing page',
                              actions: [{ label: 'Delete', danger: true, onClick: () => setBulkDeleteOpen(true) }],
                              onClear: landingSelection.clear,
                            }}
                            onRowClick={(row) => {
                              if (row.kind === 'site') {
                                setSelectedUrl(row.page.url);
                                setSelectedGeneratedId(null);
                              } else {
                                setSelectedGeneratedId(row.generated.public_id);
                              }
                            }}
                            getRowClassName={(row) =>
                              row.kind === 'site' && selectedUrl === row.page.url && !selectedGeneratedId ? 'bg-[#290BD9]/10' : ''
                            }
                            emptyState="No pages match the filter."
                          />
                        </div>
                      </div>
                      <div className="relative col-span-3 p-6">
                        {/* The hint that nothing is chosen centres itself in
                            the panel, bounded by the viewport as well, so a
                            long table does not push it off screen (#6743). */}
                        <div className={selectedLandingPage ? 'sticky top-6' : 'absolute inset-6 flex items-start justify-center'}>
                          {selectedLandingPage ? (
                            <div className="space-y-4">
                              <div>
                                <h3 className="text-lg font-semibold text-gray-900">
                                  {selectedLandingPage.title || getPageTitle(selectedLandingPage.url)}
                                </h3>
                                <a
                                  href={selectedLandingPage.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-sm text-[#290BD9] hover:underline break-all"
                                >
                                  {selectedLandingPage.url}
                                </a>
                              </div>

                              {selectedLandingPage.description && (
                                <div>
                                  <div className="flex items-center gap-2 mb-2">
                                    <Sparkles className="h-4 w-4 text-[#290BD9]" />
                                    <span className="text-xs text-gray-700 font-medium">What the page says</span>
                                  </div>
                                  <p className="text-sm text-gray-900">
                                    {selectedLandingPage.description}
                                  </p>
                                </div>
                              )}

                              {selectedLandingPage.intent && (
                                <div>
                                  <div className="flex items-center gap-2 mb-2">
                                    <Sparkles className="h-4 w-4 text-[#290BD9]" />
                                    <span className="text-xs text-gray-700 font-medium">What it is for</span>
                                  </div>
                                  <p className="text-sm text-gray-900">
                                    {selectedLandingPage.intent}
                                  </p>
                                </div>
                              )}

                              {!selectedLandingPage.description && !selectedLandingPage.intent && (
                                <p className="text-sm text-gray-500">
                                  Nothing has been read from this landing page yet.
                                </p>
                              )}
                            </div>
                          ) : (
                            <EmptyState
                              className="border-0 shadow-none"
                              compact
                              icon={<Globe className="h-12 w-12 text-gray-500" strokeWidth={1} />}
                              title="Select a Landing Page"
                              description="Click any row in the table to see what Glitch read from the page"
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  )
                )}

                {/* Media Tab */}
                {activeTab === 'media' && (
                  <ImageGallery mode='library' onControlsReady={setMediaControls} />
                )}

                {/* Documents Tab (#6232): the business documents and calls the
                    strategy report is written from. */}
                {activeTab === 'documents' && business?.id && (
                  <DocumentsLibrary businessId={business.id} onControlsReady={setDocumentsControls} />
                )}

                {/* Conversions Tab */}
                {activeTab === 'conversions' && <ConversionManagement onControlsReady={setConversionsControls} />}

                {/* Google Tab — one sub-tab per account-level asset section. Order mirrors the
                    campaign Misc tab: Sitelinks → Callouts → Negatives. Each tab
                    label carries a purple count badge; the Add button is hoisted to the right of
                    the tabs and changes label per active sub-tab. */}
                {activeTab === 'google' && (() => {
                  if (googleDataPending) {
                    return (
                      <div className="flex items-center justify-center gap-3 min-h-[50vh]">
                        <Spinner size="md" className="text-gray-500" />
                        <span className="text-gray-500">Loading Google assets...</span>
                      </div>
                    );
                  }
                  if (googleTotalCount === 0) {
                    return (
                      <div className="flex flex-col items-center justify-center min-h-[50vh] text-center">
                        <Globe className="h-12 w-12 text-gray-500 mb-4" strokeWidth={1} />
                        <h3 className="text-lg font-semibold text-gray-500 mb-2">No Google Assets</h3>
                        <p className="text-gray-500 mb-6">Sitelinks, callouts, negative keywords, and IP exclusions will appear here once they're added</p>
                        <Button
                          onClick={() => navigate({ to: '/settings/account', hash: 'integrations' })}
                          size="lg"
                          className="w-[280px] py-6 bg-[#290BD9] hover:bg-[#2309C5] text-white shadow-lg flex items-center justify-center space-x-2"
                        >
                          <span>SELECT ACCOUNT</span>
                          <ArrowRight className="h-4 w-4" />
                        </Button>
                      </div>
                    );
                  }
                  return (
                    <div>
                      {/* -mt-2.5 cancels the sub-tab buttons' top padding so the space above the
                          tab text matches the space below the divider (#4812). The section's Add
                          button is in the page toolbar (#6681). Nothing sits between the divider
                          and the table's header row (#6715). */}
                      {/* The rule runs the card's full width, like the tables
                          below it; the labels keep the card's own inset. */}
                      <div className="-mx-6 px-6 -mt-2.5 border-b border-gray-200 flex items-end">
                        <div className="flex gap-0">
                          {googleSubTabConfigs.map((tab) => {
                            const isActive = activeGoogleSubTab === tab.id;
                            return (
                              <button
                                key={tab.id}
                                type="button"
                                onClick={() => { setGoogleSubTab(tab.id); setSavedGoogleSubTab(tab.id); }}
                                className={`px-5 py-2.5 text-sm font-medium transition-all border-b-2 -mb-px ${
                                  isActive
                                    ? 'border-[#290BD9] text-gray-900'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                              >
                                {tab.label}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {activeGoogleSubTab === 'sitelinks' && <AccountSitelinks ref={accountSitelinksRef} filter={sitelinkFilter} />}
                      {activeGoogleSubTab === 'callouts' && <AccountCallouts ref={accountCalloutsRef} filter={calloutFilter} />}
                      {activeGoogleSubTab === 'negatives' && (
                        <NegativeKeywordLists
                          ref={accountNegativesRef}
                          selectedListId={selectedNegativeListId}
                          onSelectList={setSelectedNegativeListId}
                        />
                      )}
                      {activeGoogleSubTab === 'ip-exclusions' && <AccountIpExclusions ref={accountIpExclusionsRef} filter={ipExclusionFilter} />}
                    </div>
                  );
                })()}

                {/* Audiences Tab */}
                {activeTab === 'audiences' && (
                  <div className="flex flex-col items-center justify-center min-h-[50vh] text-center">
                    <Users className="h-12 w-12 text-gray-500 mb-4" strokeWidth={1} />
                    <h3 className="text-lg font-semibold text-gray-500 mb-2">Audience Lists</h3>
                    <p className="text-gray-500">Coming soon</p>
                  </div>
                )}
          </TabCardPanel>
          </div>
          {isNegativesDetail && (
            <div className="col-span-4 min-w-0">
              <div className="sticky top-0 mt-[42px]">
                <NegativeKeywordDetail listId={selectedNegativeListId} />
              </div>
            </div>
          )}
          </div>
      </div>

      {business && (
        <AddLandingPageModal
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          businessId={business.id}
          organizationId={organizationData?.id ?? 0}
          existingUrls={landingPagesDetails.map(lp => lp.url)}
        />
      )}

      {business && (
        <GenerateLandingPageDialog
          open={isGenerateLandingPageOpen}
          onOpenChange={setIsGenerateLandingPageOpen}
          businessId={business.id}
          organizationId={organizationData?.id ?? 0}
          icps={business.icps ?? []}
        />
      )}

      <ConformationModal
        isOpen={bulkDeleteOpen}
        onClose={() => !bulkDeleting && setBulkDeleteOpen(false)}
        onConfirm={() => void bulkDeleteLandingPages()}
        title="Delete Landing Pages"
        description={`Are you sure you want to remove ${landingSelection.count === 1 ? 'this landing page' : `these ${landingSelection.count} landing pages`}? Existing ads using these URLs will not be affected.`}
        confirmText="Delete"
        isLoading={bulkDeleting}
      />
      <ConformationModal
        isOpen={!!deleteUrl}
        onClose={() => setDeleteUrl(null)}
        onConfirm={() => deleteUrl && deleteMutation.mutate(deleteUrl)}
        title="Delete Landing Page"
        description={`Are you sure you want to remove this landing page? Existing ads using this URL will not be affected.`}
        confirmText="Delete"
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
}
